import { OpenAcademicTranscript } from '@/components/buttons/open-academic-transcript'
import { RequestActionButton } from '@/components/buttons/request-action-button'
import { RequestData } from '@/components/cards/request-data'
import { ViewStudentObservation } from '@/components/cards/view-student-observation'
import { GrantReviewModal } from '@/components/dialogs/grant-review-modal'
import { NotGrantReviewModal } from '@/components/dialogs/not-grant-review-modal'
import { PageContent } from '@/components/layout/page-content'
import { PageHeader } from '@/components/layout/page-header'
import { PageLayout } from '@/components/layout/page-layout'
import { Show } from '@/components/show'
import { CoursesEquivalences } from '@/components/tables/courses-quivalences'
import { SimulatedTranscript } from '@/components/tables/simulated-transcript'
import { RequestStatus } from '@/components/titles/request-status'
import { RequestTitle } from '@/components/titles/request-title'
import { Button } from '@/components/ui/button'
import { REQUEST_STATUS } from '@/constants/request-status'
import type { Curriculum } from '@/types/courses/curriculum'
import { formatRequestProtocol } from '@/utils/format-request-protocol'
import { getRequestStatusById } from '@/utils/get-status-by-id'
import { useChangeOfMajorReview } from './hooks/use-change-of-major-review'
import { useChangeOfMajorReviewForms } from './hooks/use-change-of-major-review-form'

export const ChangeOfMajorReviewPage = () => {
  const {
    states: {
      isLoading,
      request,
      allCurriculums,
      currentCurriculum,
      curriculumCourses,
      previousCourses,
      isOpenGrantDialog,
      grantIsPending,
      grantSuccess,
      isOpenNotGrantDialog,
      denyIsPending,
      denySuccess
    },
    actions: {
      setCurrentCurriculum,
      setIsOpenGrantDialog,
      grantMutate,
      denyMutate,
      setIsOpenNotGrantDialog
    }
  } = useChangeOfMajorReview()

  const {
    states: { formMethods },
    actions: { clearDesiredCourses }
  } = useChangeOfMajorReviewForms(request)

  const submitGrant = () => {
    if (formMethods && formMethods.handleSubmit) {
      formMethods.handleSubmit((data) => grantMutate(data))()
    }
  }

  const submitDeny = () => {
    if (formMethods && formMethods.handleSubmit) {
      formMethods.handleSubmit((data) => denyMutate(data))()
    }
  }

  const handleCurrentCurriculumChange = (curriculum: Curriculum) => {
    setCurrentCurriculum(curriculum)

    const equivalences = request?.courseEquivalences.length || 0
    const formFields =
      formMethods?.getValues?.('courseEquivalences').length || 0
    for (let i = formFields - 1; i >= equivalences; i--) {
      formMethods?.remove?.(i)
    }
    clearDesiredCourses()
  }

  const disableActions =
    request?.requestStatus.id !== REQUEST_STATUS.UNDER_REVIEW.id

  const status = getRequestStatusById(request?.requestStatus.id)

  return (
    <PageLayout isLoading={isLoading} loadingText="Carregando Análise">
      <PageHeader title="Minhas Análises" breadcrumb>
        <Button disabled className="w-38 bg-[var(--brand-green)] select-none">
          Iniciar Análise
        </Button>
      </PageHeader>
      <PageContent>
        <div className="w-full mb-6 mt-6 flex justify-between">
          <div className="flex gap-5 items-center">
            <div className="flex items-center gap-2">
              <RequestTitle title="Análise" />
              <RequestTitle
                title={`#${formatRequestProtocol(request?.protocol)}`}
              />
            </div>
            <RequestStatus name={status?.name} color={status?.color} />
          </div>
          <div className="flex items-center gap-10">
            <div>
              <OpenAcademicTranscript
                link={request?.previousEducation?.academicTranscript || '#'}
              />
            </div>
            <RequestActionButton
              requestId=""
              buttonDisabled={disableActions}
              grantActive={true}
              denyActive={true}
              denyAction={() => setIsOpenNotGrantDialog(true)}
              grantAction={() => setIsOpenGrantDialog(true)}
              formMethods={formMethods}
            />
          </div>
        </div>
        <RequestData
          createdAt={request?.createdAt}
          desiredCourse={request?.desiredEducation?.desiredEducation}
          name={request?.student?.person?.name}
          previousCourse={request?.previousEducation?.name}
          ra={request?.student?.ra}
          status={request?.requestStatus?.name}
        />

        <div className="mt-12">
          <CoursesEquivalences
            currentCurriculum={currentCurriculum}
            curriculumCourses={curriculumCourses}
            curriculums={allCurriculums}
            setCurrentCurriculum={handleCurrentCurriculumChange}
            request={request}
            formMethods={formMethods}
            previousCourses={previousCourses}
            onlyView={disableActions}
          />
        </div>

        <div className="w-full mt-12 mb-6">
          <RequestTitle title="Simulação do Novo Histórico do Aluno" />
        </div>

        <SimulatedTranscript
          subTitle={request?.desiredEducation?.desiredEducation}
          courses={curriculumCourses}
          formMethods={formMethods}
          requestStatus={request?.requestStatus.id}
        />

        <GrantReviewModal
          formMethods={formMethods}
          isOpen={isOpenGrantDialog && !grantSuccess}
          setOpen={setIsOpenGrantDialog}
          grantIsPending={grantIsPending}
          grantSubmit={submitGrant}
        />

        <NotGrantReviewModal
          formMethods={formMethods}
          isOpen={isOpenNotGrantDialog && !denySuccess}
          setOpen={setIsOpenNotGrantDialog}
          notGrantIsPending={denyIsPending}
          notGrantSubmit={submitDeny}
        />

        <Show when={!!request?.observation}>
          <div className="py-10">
            <ViewStudentObservation
              requestStatusId={request?.requestStatus.id}
              observation={request?.observation}
            />
          </div>
        </Show>
      </PageContent>
    </PageLayout>
  )
}
